"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.facilitiesSchema = exports.facilitiesQuerySchema = exports.cliniciansSchema = exports.cliniciansQuerySchema = exports.reportMeasuresSchema = exports.reportMeasuresQuerySchema = exports.gapPageSchema = exports.gapTableWorkbookRequestSchema = exports.gapPageRequestSchema = exports.GAP_SORT_COLUMNS = exports.GAP_PAGE_SIZE = exports.reportSummarySchema = exports.reportSummaryQuerySchema = exports.gapClosureWorkbookRequestSchema = exports.gapClosureWorkbookQuerySchema = exports.acoPracticeMatrixSchema = exports.gapReportSchema = exports.consolidatedRowSchema = exports.gapReportRowSchema = exports.practicePerformanceSchema = exports.practicesSchema = exports.NOT_IN_ORGANIZATION = exports.practiceSchema = exports.gapsQuerySchema = exports.FACILITY_NOT_KNOWN = exports.practicePerformanceQuerySchema = exports.practiceParamsSchema = exports.practicesQuerySchema = exports.ALL_ACOS = exports.GAP_SHOW = void 0;
// Wire shapes of the practice view and the quality report (CR-002 §7; CR2-020, CR2-022, CR2-054,
// CR2-061–CR2-067, CR2-082; J-083), served at `/api/v1/practices`, `/api/v1/practices/:tin/performance`
// and `/api/v1/gaps` (D-973). Numbers are raw (SPEC §9); the web formats them. The practice routes
// carry counts only; `/gaps` carries patients' names and dates of birth and is a PHI route (CR2-067).
const zod_1 = require("zod");
const inactive_js_1 = require("../quality-report/inactive.js");
const TIN = /^\d{9}$/;
const NPI = /^\d{10}$/;
const MEASURE_KEY = /^[1-9]\d{0,9}$/;
const CONSOLIDATED = "consolidated";
/** The report's "Show" choice (the owner's inbox file 05): every patient, or patients with open gaps. */
exports.GAP_SHOW = ["all", "open"];
/** The gap statuses a report filter may name (NOT_IN_DENOMINATOR gaps are never listed). */
const LISTED_GAP_STATUSES = [
    "OPEN",
    "CLOSED",
    "EXCLUDED",
    "EXCEPTION",
    "REOPENED",
    "PENDING_REVIEW",
];
const yearSchema = zod_1.z.coerce.number().int().min(2000).max(2100);
const acoCodeSchema = zod_1.z.string().min(1).max(64);
/**
 * J-271 (Louis, QB3 item 9a): the quality report's `aco` for every ACO in the caller's scope, the
 * header's "All". Only an ACO-wide (aco-set) caller may send it: a group or practice user, who has no
 * "All" (J-251), is answered 404 as for an ACO outside its scope, so the scope never widens.
 */
exports.ALL_ACOS = "ALL";
/**
 * J-232 (the owner's inbox file 33, R5): one performance group's id, as `/performance-groups/:groupId`
 * names it. A row belongs to the group when `core.group_patient_measure` holds (group, measure, patient),
 * the membership the group figures use. Omitted, or the ACO-wide APM_ENTITY group, means no group filter.
 */
const groupFilterSchema = zod_1.z.coerce.number().int().positive().max(2_147_483_647).optional();
// No `.meta({ id })` on a querystring or params schema: @fastify/swagger expands parameters inline.
exports.practicesQuerySchema = zod_1.z.object({
    year: yearSchema,
    /** One ACO's code; omitted means every ACO in the caller's scope. */
    aco: acoCodeSchema.optional(),
    /** J-232: only the TINs whose patients are in this group (needs `aco`). */
    group: groupFilterSchema,
});
exports.practiceParamsSchema = zod_1.z.object({ tin: zod_1.z.string().regex(TIN, "a 9-digit TIN") });
exports.practicePerformanceQuerySchema = zod_1.z.object({
    year: yearSchema,
    aco: acoCodeSchema,
    /** CR2-054: only the gaps whose owner clinician is this NPI. */
    npi: zod_1.z.string().regex(NPI, "a 10-digit NPI").optional(),
    /** J-232: only this group's patients and clinicians. */
    group: groupFilterSchema,
});
/**
 * J-275 (QB3 item 13c): the Facility filter's value in a query or body: a facility's name as the bundle
 * gives it, or `FACILITY_NOT_KNOWN` for the rows whose facility is unknown. Not PHI (a place, never a
 * patient), but like any query value it is never logged.
 */
exports.FACILITY_NOT_KNOWN = "~not-known";
const facilityFilterSchema = zod_1.z.string().min(1).max(300).optional();
exports.gapsQuerySchema = zod_1.z.object({
    year: yearSchema,
    aco: acoCodeSchema,
    /** One owner TIN; omitted means every TIN in scope (the consolidated ACO view, CR2-061). */
    tin: zod_1.z.string().regex(TIN, "a 9-digit TIN").optional(),
    npi: zod_1.z.string().regex(NPI, "a 10-digit NPI").optional(),
    group: groupFilterSchema,
    /** J-275: one facility's rows, or `FACILITY_NOT_KNOWN`'s. */
    facility: facilityFilterSchema,
    /** `consolidated`, or a measure key for that measure's tab (CR2-060). */
    tab: zod_1.z
        .string()
        .refine((t) => t === CONSOLIDATED || MEASURE_KEY.test(t), "consolidated or a measure key"),
    /** On the consolidated tab: only patients in this measure's population. */
    measureKey: zod_1.z.coerce.number().int().positive().max(2_147_483_647).optional(),
    status: zod_1.z.enum(LISTED_GAP_STATUSES).optional(),
});
/**
 * A practice to choose: a configured one, or (J-209, inbox 06 PR D item 4) a TIN that owns gaps the
 * configuration does not name, named from the ACO Participant List or "Not in the organization".
 */
exports.practiceSchema = zod_1.z
    .object({
    acoCode: zod_1.z.string(),
    tin: zod_1.z.string(),
    practiceName: zod_1.z.string(),
    /** False for an owner TIN the organization document does not name. */
    configured: zod_1.z.boolean(),
})
    .meta({ id: "AcoPractice" });
/** The name shown for an owner TIN that neither the organization nor the Participant List names. */
exports.NOT_IN_ORGANIZATION = "Not in the organization";
exports.practicesSchema = zod_1.z
    .object({ performanceYear: zod_1.z.number().int(), practices: zod_1.z.array(exports.practiceSchema) })
    .meta({ id: "AcoPractices" });
const measureIdentity = {
    measureKey: zod_1.z.number().int(),
    measureNumber: zod_1.z.string(),
    displayNumber: zod_1.z.string(),
    shortLabel: zod_1.z.string(),
    isInverse: zod_1.z.boolean(),
};
/** CR2-020's figures for one measure over the gaps in scope. */
const figures = {
    numerator: zod_1.z.number().int(),
    denominator: zod_1.z.number().int(),
    excluded: zod_1.z.number().int(),
    exceptions: zod_1.z.number().int(),
    openGaps: zod_1.z.number().int(),
    /** 100 × numerator ÷ denominator; null for a zero denominator. */
    performanceRate: zod_1.z.number().nullable(),
};
exports.practicePerformanceSchema = zod_1.z
    .object({
    acoCode: zod_1.z.string(),
    performanceYear: zod_1.z.number().int(),
    tin: zod_1.z.string(),
    /** `ref.practice.practice_name`; null for a TIN the configuration does not name. */
    practiceName: zod_1.z.string().nullable(),
    npi: zod_1.z.string().nullable(),
    /** ISO 8601: the newest COMPLETE batch's load, and the newest calculation. */
    dataLoadedAt: zod_1.z.string().nullable(),
    calculatedAt: zod_1.z.string().nullable(),
    /** The clinicians who own the TIN's gaps, by NPI: the clinician filter's choices (CR2-054). */
    clinicians: zod_1.z.array(zod_1.z.object({ npi: zod_1.z.string(), name: zod_1.z.string().nullable() })),
    measures: zod_1.z.array(zod_1.z.object({ ...measureIdentity, title: zod_1.z.string(), ...figures })),
})
    .meta({ id: "AcoPracticePerformance" });
const patientColumns = {
    acoCode: zod_1.z.string(),
    tin: zod_1.z.string(),
    practiceName: zod_1.z.string().nullable(),
    /** `core.patient_source_id.source_patient_id` (CR-002 §6.9 Member ID). */
    memberId: zod_1.z.string().nullable(),
    firstName: zod_1.z.string(),
    lastName: zod_1.z.string(),
    /** ISO `YYYY-MM-DD`. */
    dob: zod_1.z.string(),
    deceased: zod_1.z.boolean().nullable(),
    /**
     * J-262 (C4, the owner 2026-10-16: "inactive Yes if patient died or was discharged"); J-274 (NR-05): a
     * date of death or of discharge is on file. Never null: N is the default.
     */
    inactive: zod_1.z.boolean(),
    /**
     * J-271 (QB3 item 9b): the patient's performance groups for the row's measure (Consolidated: for any
     * of the line's measures), the ACO-wide group excluded, names joined with '; '; null for none. A group
     * user's rows name only its granted groups, a practice user's none (J-251).
     */
    performanceGroup: zod_1.z.string().nullable(),
    /** J-274: why the patient is inactive, Deceased winning when both dates are on file; null when active. */
    inactiveReason: zod_1.z.enum(inactive_js_1.INACTIVE_REASONS).nullable(),
    /** J-274: the date of death, or else of discharge, ISO `YYYY-MM-DD`; null when active. */
    inactiveDate: zod_1.z.string().nullable(),
    /**
     * J-275 (QB3 item 13b): the facility of the patient's latest qualifying visit in the year, as the
     * bundle names it; null when unknown.
     */
    facility: zod_1.z.string().nullable(),
};
const clinicianSchema = zod_1.z.object({ npi: zod_1.z.string(), name: zod_1.z.string().nullable() });
/** CR-002 §6.10: one row of a measure tab. */
exports.gapReportRowSchema = zod_1.z
    .object({
    ...patientColumns,
    /** `core.patient.patient_id`, the opaque id Screen 4's link carries (J-186); not in the workbook. */
    patientId: zod_1.z.uuid(),
    clinician: clinicianSchema.nullable(),
    gapStatus: zod_1.z.enum(LISTED_GAP_STATUSES),
    /** The rule's key, label, action and gap flag (CR2-064). */
    ruleKey: zod_1.z.string().nullable(),
    statusLabel: zod_1.z.string(),
    action: zod_1.z.string(),
    gap: zod_1.z.boolean(),
    /** `Y` for every registry row (CR2-082). */
    qualifyingEncounter: zod_1.z.literal("Y"),
    qeCode: zod_1.z.string().nullable(),
    qeDate: zod_1.z.string().nullable(),
    exclusionCode: zod_1.z.string().nullable(),
    exclusionDate: zod_1.z.string().nullable(),
    numeratorCode: zod_1.z.string().nullable(),
    numeratorDate: zod_1.z.string().nullable(),
    /** `{systolic}/{diastolic}`, #236 only. */
    bp: zod_1.z.string().nullable(),
    dxCode: zod_1.z.string().nullable(),
    dxDate: zod_1.z.string().nullable(),
    deathDate: zod_1.z.string().nullable(),
})
    .meta({ id: "AcoGapReportRow" });
/** CR-002 §6.9: one row per patient and owner TIN, a consolidated label per measure. */
exports.consolidatedRowSchema = zod_1.z
    .object({
    ...patientColumns,
    /**
     * J-373 (Louis, 2026-10-28): the line's patient, `core.patient.patient_id`, the id the why pop-up reads
     * by; every row of a line is one patient (the line is ACO, patient and owner TIN). Not in the workbook.
     */
    patientId: zod_1.z.uuid(),
    clinicians: zod_1.z.array(clinicianSchema),
    /**
     * Per measure column: the consolidated label, or null ('—') outside the row's population; J-358: the
     * cell's open-gap flag and rule key (false and null outside the population), for its chip's colour.
     */
    statuses: zod_1.z.array(zod_1.z.object({
        measureKey: zod_1.z.number().int(),
        label: zod_1.z.string().nullable(),
        gap: zod_1.z.boolean(),
        ruleKey: zod_1.z.string().nullable(),
        /**
         * J-373: the measure tab row's Action to Take for this cell ('' outside the population), which the
         * why pop-up repeats for an open gap. Not in the workbook.
         */
        action: zod_1.z.string(),
    })),
    /** J-203: one or more of the line's measures is an open gap (the workbook's Gap column). */
    gap: zod_1.z.boolean(),
})
    .meta({ id: "AcoConsolidatedRow" });
exports.gapReportSchema = zod_1.z
    .object({
    acoCode: zod_1.z.string(),
    performanceYear: zod_1.z.number().int(),
    tin: zod_1.z.string().nullable(),
    npi: zod_1.z.string().nullable(),
    tab: zod_1.z.string(),
    /** The measures in the report, in display order, with CR2-062's summary line for the scope. */
    measures: zod_1.z.array(zod_1.z.object({ ...measureIdentity, ...figures })),
    consolidatedRows: zod_1.z.array(exports.consolidatedRowSchema),
    rows: zod_1.z.array(exports.gapReportRowSchema),
})
    .meta({ id: "AcoGapReport" });
/** One row of Screen 1's matrix (CR-002 CR2-030, CR2-031): a practice TIN, or the ACO's TOTAL. */
const matrixRowSchema = zod_1.z.object({
    /** Null on the TOTAL row. */
    tin: zod_1.z.string().nullable(),
    /** `ref.practice.practice_name`, else the participant list's legal business name; null if neither. */
    name: zod_1.z.string().nullable(),
    /** True for a TIN `ref.practice` configures; false for another TIN that owns a gap, and the TOTAL. */
    configured: zod_1.z.boolean(),
    isTotal: zod_1.z.boolean(),
    figures: zod_1.z.array(zod_1.z.object({ measureKey: zod_1.z.number().int(), ...figures })),
});
exports.acoPracticeMatrixSchema = zod_1.z
    .object({
    acoCode: zod_1.z.string(),
    performanceYear: zod_1.z.number().int(),
    dataLoadedAt: zod_1.z.string().nullable(),
    calculatedAt: zod_1.z.string().nullable(),
    measures: zod_1.z.array(zod_1.z.object({ ...measureIdentity, title: zod_1.z.string() })),
    /** Configured practices by name, then other owner TINs, then the TOTAL row (CR2-021). */
    rows: zod_1.z.array(matrixRowSchema),
})
    .meta({ id: "AcoPracticeMatrix" });
/**
 * `GET /gap-closure-workbooks?year&aco&tin&npi&group` (J-261, oversight/gap-closure/DESIGN.md): the Gap
 * Closure Workbook of the scope, one row per open gap the caller sees, with the registry's upload tab.
 */
exports.gapClosureWorkbookQuerySchema = zod_1.z.object({
    year: yearSchema,
    aco: acoCodeSchema,
    tin: zod_1.z.string().regex(TIN, "a 9-digit TIN").optional(),
    npi: zod_1.z.string().regex(NPI, "a 10-digit NPI").optional(),
    group: groupFilterSchema,
    /** J-275: the screen's Facility choice. */
    facility: facilityFilterSchema,
});
/**
 * `POST /gap-closure-workbooks` (J-272, Louis's "populated with gaps from what is viewed"): the Gap
 * Closure Workbook of the open gaps the Quality screen lists under every current selection: the scope,
 * the open tab (Consolidated: every measure; a measure key: that measure), a measure tab's Status and
 * Action to take, Show and the search box. A POST because the search can be a patient's name or member
 * id, which never goes into a URL (`.claude/rules/phi.md` rule 2), as for `POST /gap-pages`.
 */
exports.gapClosureWorkbookRequestSchema = zod_1.z.object({
    year: yearSchema,
    aco: acoCodeSchema,
    tin: zod_1.z.string().regex(TIN, "a 9-digit TIN").optional(),
    npi: zod_1.z.string().regex(NPI, "a 10-digit NPI").optional(),
    group: groupFilterSchema,
    /** J-275: the screen's Facility choice, one of the selections the file follows. */
    facility: facilityFilterSchema,
    tab: zod_1.z
        .string()
        .refine((t) => t === CONSOLIDATED || MEASURE_KEY.test(t), "consolidated or a measure key"),
    /** A measure tab's status label, as the report words it; Consolidated has none, as on the screen. */
    statusLabel: zod_1.z.string().min(1).max(300).optional(),
    /** A measure tab's action to take, as the report words it. */
    action: zod_1.z.string().min(1).max(500).optional(),
    /** The search box: matched as `POST /gap-pages` matches it (last name, first name, member id). */
    search: zod_1.z.string().trim().min(1).max(100).optional(),
    /** J-274: the Inactive and Inactive reason choices, as `POST /gap-pages` takes them. */
    inactive: zod_1.z.enum(["Y", "N"]).optional(),
    inactiveReason: zod_1.z.enum(inactive_js_1.INACTIVE_REASONS).optional(),
    show: zod_1.z.enum(exports.GAP_SHOW).default("all"),
});
/**
 * `GET /acos/:acoCode/report-summary?year&tin&npi` (J-203, the owner's inbox file 06): the Quality
 * report's Summary counts per measure for the scope, without a patient row, so a user without PHI
 * access still sees them. Counts only: not a PHI route.
 */
exports.reportSummaryQuerySchema = zod_1.z.object({
    year: yearSchema,
    tin: zod_1.z.string().regex(TIN, "a 9-digit TIN").optional(),
    npi: zod_1.z.string().regex(NPI, "a 10-digit NPI").optional(),
    group: groupFilterSchema,
    /** J-275: the screen's Facility choice. */
    facility: facilityFilterSchema,
});
exports.reportSummarySchema = zod_1.z
    .object({
    acoCode: zod_1.z.string(),
    performanceYear: zod_1.z.number().int(),
    tin: zod_1.z.string().nullable(),
    npi: zod_1.z.string().nullable(),
    measures: zod_1.z.array(zod_1.z.object({ ...measureIdentity, ...figures })),
})
    .meta({ id: "AcoReportSummary" });
// ---- the paged quality report (J-201) ----
/** Rows per page of the quality report (J-201, the owner's production review). */
exports.GAP_PAGE_SIZE = 200;
/** Columns the paged report sorts by; `default` is CR2-062/CR2-063's order (J-271: ACO, group). */
exports.GAP_SORT_COLUMNS = [
    "default",
    "aco",
    "performanceGroup",
    "practice",
    "name",
    "dob",
    "memberId",
    "status",
    // J-274: the Inactive columns sort like the others.
    "inactive",
    "inactiveReason",
    "inactiveDate",
    // J-275: so does Facility.
    "facility",
];
/**
 * `POST /gap-pages`: one page of a tab. A POST because the search text can be a patient's name or
 * member id, which never goes into a URL (`.claude/rules/phi.md` rule 2).
 */
exports.gapPageRequestSchema = zod_1.z.object({
    year: yearSchema,
    aco: acoCodeSchema,
    tin: zod_1.z.string().regex(TIN, "a 9-digit TIN").optional(),
    npi: zod_1.z.string().regex(NPI, "a 10-digit NPI").optional(),
    group: groupFilterSchema,
    /** J-275: the screen's Facility choice; it narrows the summary, the counts and every tab. */
    facility: facilityFilterSchema,
    tab: zod_1.z
        .string()
        .refine((t) => t === CONSOLIDATED || MEASURE_KEY.test(t), "consolidated or a measure key"),
    measureKey: zod_1.z.number().int().positive().max(2_147_483_647).optional(),
    status: zod_1.z.enum(LISTED_GAP_STATUSES).optional(),
    /** A measure tab's status label, as the report words it. */
    statusLabel: zod_1.z.string().min(1).max(300).optional(),
    /** A measure tab's action to take, as the report words it. */
    action: zod_1.z.string().min(1).max(500).optional(),
    /** Matched, ignoring case, against the last name, first name and member id. */
    search: zod_1.z.string().trim().min(1).max(100).optional(),
    /** J-274: only inactive (`Y`) or only active (`N`) patients' rows. */
    inactive: zod_1.z.enum(["Y", "N"]).optional(),
    /** J-274: only the rows of patients inactive for this reason. */
    inactiveReason: zod_1.z.enum(inactive_js_1.INACTIVE_REASONS).optional(),
    sort: zod_1.z
        .object({ column: zod_1.z.enum(exports.GAP_SORT_COLUMNS), direction: zod_1.z.enum(["asc", "desc"]) })
        .optional(),
    page: zod_1.z.number().int().min(1).max(1_000_000).default(1),
    /** `open`: only rows whose report rule is an open gap (Consolidated: lines with one or more). */
    show: zod_1.z.enum(exports.GAP_SHOW).default("all"),
});
/**
 * `POST /gap-table-workbooks` (J-382, Louis 2026-10-29): Quality Detail's "Download", the open tab's table as
 * the screen shows it, every matching row over all pages: `POST /gap-pages`' request without `page`. A POST
 * for the same reason (the search can be a name).
 */
exports.gapTableWorkbookRequestSchema = exports.gapPageRequestSchema.omit({ page: true });
exports.gapPageSchema = exports.gapReportSchema
    .extend({
    page: zod_1.z.number().int(),
    pageSize: zod_1.z.number().int(),
    /** Rows (a measure tab) or patient and practice lines (Consolidated) that match, over all pages. */
    total: zod_1.z.number().int(),
    /**
     * A measure tab's status labels in the scope with each one's row count, for the Status filter,
     * counted over every page under the Show choice; empty on Consolidated.
     */
    statusLabels: zod_1.z.array(zod_1.z.object({ label: zod_1.z.string(), count: zod_1.z.number().int() })),
    actions: zod_1.z.array(zod_1.z.string()),
    /**
     * J-358 (the quality expert, 2026-10-27): Quality Detail's three cards, over every status cell the
     * selections match on every page (a measure tab's rows; Consolidated's lines' patient-measure cells):
     * open gaps, exceptions or exclusions, and the rest (met).
     */
    tally: zod_1.z.object({
        open: zod_1.z.number().int(),
        exceptionOrExclusion: zod_1.z.number().int(),
        met: zod_1.z.number().int(),
    }),
})
    .meta({ id: "AcoGapPage" });
/**
 * `GET /acos/:acoCode/report-measures?year` (J-201, the owner's inbox file 06): the quality report's
 * measures for its tabs, without reading a gap: the screen no longer waits on Consolidated for them.
 */
exports.reportMeasuresQuerySchema = zod_1.z.object({ year: yearSchema });
exports.reportMeasuresSchema = zod_1.z
    .object({
    measures: zod_1.z.array(zod_1.z.object({
        measureKey: zod_1.z.number().int(),
        measureNumber: zod_1.z.string(),
        displayNumber: zod_1.z.string(),
        shortLabel: zod_1.z.string(),
    })),
})
    .meta({ id: "AcoReportMeasures" });
/**
 * `GET /acos/:acoCode/clinicians?year&tin` (J-209, inbox 06 PR D item 15): the owner clinicians of the
 * scope's gaps, by NPI, for the quality report's clinician filter with or without a practice. Provider
 * names and NPIs only; no patient.
 */
exports.cliniciansQuerySchema = zod_1.z.object({
    year: yearSchema,
    tin: zod_1.z.string().regex(TIN, "a 9-digit TIN").optional(),
    /** J-238: only the owner clinicians of this group's gaps (a group user: its first granted group by default). */
    group: groupFilterSchema,
});
exports.cliniciansSchema = zod_1.z
    .object({
    acoCode: zod_1.z.string(),
    performanceYear: zod_1.z.number().int(),
    clinicians: zod_1.z.array(clinicianSchema),
})
    .meta({ id: "AcoClinicians" });
/**
 * `GET /acos/:acoCode/facilities?year&tin&group` (J-275, QB3 item 13c): the Facility filter's choices,
 * the facilities of the scope's rows (the ACO, the performance group and the practice; never the
 * clinician or the facility itself), each with how many patients, and how many patients' facility is
 * unknown. Places and counts only: no patient.
 */
exports.facilitiesQuerySchema = zod_1.z.object({
    year: yearSchema,
    tin: zod_1.z.string().regex(TIN, "a 9-digit TIN").optional(),
    group: groupFilterSchema,
});
exports.facilitiesSchema = zod_1.z
    .object({
    acoCode: zod_1.z.string(),
    performanceYear: zod_1.z.number().int(),
    /** By name; each with its distinct patients among the scope's rows. */
    facilities: zod_1.z.array(zod_1.z.object({ name: zod_1.z.string(), patients: zod_1.z.number().int() })),
    /** Distinct patients among the scope's rows whose facility is unknown. */
    notKnown: zod_1.z.number().int(),
})
    .meta({ id: "AcoFacilities" });
//# sourceMappingURL=gaps.js.map